import classNames from 'classnames'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

// constant
import { CLASS_PREFIX } from 'src/constants/'

// interfaces
import { IKeyValue } from 'src/interfaces/'
import { IAddress } from 'src/interfaces/address'

// components
import ActionList from 'src/components/location_picker/actions/action_list'
import PlaceSearch from 'src/components/location_picker/place_search'
import { hideSnackBar, showSnackBar } from 'src/components/snack_bar/snack_bar'

// helper
import { useGeoLookup } from '@lopo-tech/lopo-api'
import { IPlaceSuggestion, PlaceSuggestionType } from 'src/utils/places/autocomplete'
import { textResources } from 'src/lang/de'
import { formatAddress } from 'src/utils/location'
import { getBrowserPosition } from 'src/utils/position'
import { toggleBodyScrolllock } from 'src/utils/scrolllock/scrolllock'
import './location_picker.scss'

const cls: string = `${CLASS_PREFIX}location-picker`
const labels = textResources.locationChanger

interface IProps {
  validateAddress?: boolean;
  area?: string;
  homeLocation?: IAddress;
  type?: PlaceSuggestionType;
  close?: () => void;
  clear?: () => void;
  onLocateMe?: () => void;
  takeMeHome?: () => void;
  onChange?: (address: IAddress) => void;
  onPopularPlacesSelect?: () => void;
  showBackground?: boolean; // default: true
  showOnlyInputWhenNotFocused?: boolean; // default: false
  autoFocusSearchInput?: boolean; // default: true
  forceShowOnlyInput?: boolean; // default: false
  preFilledAddress?: IAddress;
  onFocus?: () => void;
  onQueryChange?: (query: string) => void;
  streetRequired?: boolean;
  defaultAddress?: string;
  valid?: boolean;
  errorText?: string;
  label?: string;
  getRedirectPath?: (locationSlug?: string) => string;
  enableScroll?: boolean;
}

const LocationPicker: React.FC<IProps> = (props) => {
  const {
    enableScroll,
    autoFocusSearchInput = true,
    clear,
    close,
    forceShowOnlyInput = false,
    area,
    preFilledAddress,
    homeLocation,
    label,
    showBackground = true,
    type,
    valid,
    errorText
    // getRedirectPath
  } = props

  // state
  const [isActionListVisible, setIsActionListVisible] = useState(!(props.showOnlyInputWhenNotFocused ?? false))
  const [query, setQuery] = useState<string>(props.defaultAddress || '')

  // refs
  const scrollRef = useRef<HTMLDivElement | null>(null)

  const [runGeoLookup] = useGeoLookup()

  useEffect(() => {
    if (enableScroll) {
      toggleBodyScrolllock(true, scrollRef.current || null)
    }
    return () => {
      if (enableScroll) {
        toggleBodyScrolllock(false, scrollRef.current || null)
      }
    }
  }, [])

  const clearErrorAndChangeAddress = useCallback((address: IAddress) => {
    const { onChange } = props
    hideSnackBar()
    if (onChange) {
      onChange(address)
    }
  }, [props])

  const onSelectPlace = useCallback((suggestion: IPlaceSuggestion) => {
    const { streetRequired } = props

    setIsActionListVisible(false)
    setQuery(suggestion.text)

    runGeoLookup({
      variables: {
        googlePlaceId: suggestion.placeId,
        streetRequired: Boolean(streetRequired)
      }
    }).then(({ data }) => {
      if (data?.geoLookup && data.geoLookup.length > 0) {
        clearErrorAndChangeAddress(data.geoLookup[0])
      } else {
        showSnackBar(labels.areaNotReleased)
      }
    })
  }, [props, clearErrorAndChangeAddress])

  const searchHasResults = useCallback((value: boolean) => {
    setIsActionListVisible(!value)
  }, [])

  const locateMe = useCallback(() => {
    const { onLocateMe, streetRequired } = props

    if (onLocateMe) {
      onLocateMe()
    }

    // TODO: show loading spinner to the user while fetching address from the browser
    getBrowserPosition((position) => {
      runGeoLookup({
        variables: {
          coordinates: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          },
          streetRequired: Boolean(streetRequired)
        }
      }).then(({ data }) => {
        if (data?.geoLookup && data.geoLookup.length > 0) {
          clearErrorAndChangeAddress(data.geoLookup[0])
          setQuery(formatAddress(data.geoLookup[0]))
        } else {
          showSnackBar(labels.areaNotReleased)
        }
      })
    }, () => {
      showSnackBar(labels.enableGeolocationInfo)
    })
  }, [props, clearErrorAndChangeAddress])

  // The shortcuts set an address without putting it in the field, so the search box
  // stayed empty while a place was in fact chosen - the only sign was a button
  // turning active. This sits on each shortcut rather than in
  // `clearErrorAndChangeAddress`, which also runs behind a picked Google result:
  // there the field already holds what the person selected, and overwriting it
  // with `formatAddress` would drop the street in every consumer of this picker.
  // Setting the value alone starts no search - that hangs on `handleInputChange`.
  const takeMeHome = useCallback(() => {
    const { homeLocation, onChange, takeMeHome: takeMeHomeProp } = props
    setIsActionListVisible(false)
    hideSnackBar()

    if (typeof takeMeHomeProp !== 'undefined') {
      return takeMeHomeProp()
    }

    if (typeof homeLocation === 'undefined' || typeof onChange === 'undefined') {
      return
    }

    onChange(homeLocation)
    setQuery(formatAddress(homeLocation))
  }, [props])

  const takePrefilled = useCallback(() => {
    const { preFilledAddress, onChange } = props
    setIsActionListVisible(false)
    hideSnackBar()
    if (onChange && preFilledAddress) {
      onChange(preFilledAddress)
      setQuery(formatAddress(preFilledAddress))
    }
  }, [props])

  const onChangeQuery = useCallback((q: string) => {
    const { onQueryChange } = props
    if (onQueryChange) {
      onQueryChange(q)
    }
    setQuery(q)
  }, [props])

  const onFocus = useCallback(() => {
    setIsActionListVisible(true)

    const { onFocus: onFocusProp } = props
    if (typeof onFocusProp !== 'undefined') {
      onFocusProp()
    }
  }, [props])

  const classes: IKeyValue = useMemo(() => {
    const c: IKeyValue = {}
    c[cls] = true
    c[`${cls}--active`] = !forceShowOnlyInput
    c[`${cls}--show-background`] = showBackground
    return c
  }, [forceShowOnlyInput, showBackground])

  return (
    <div ref={scrollRef} className={classNames(classes)}>
      <div className={`${cls}__content`}>
        <PlaceSearch
          autoFocus={autoFocusSearchInput}
          area={area}
          onChangeQuery={onChangeQuery}
          onSelect={onSelectPlace}
          onFocus={onFocus}
          onHasResults={searchHasResults}
          clear={clear}
          close={close}
          floatLabel={false}
          placeholder={label}
          type={type}
          query={query}
          valid={valid}
          errorText={errorText}
          noBackground={!showBackground}
        />

        {!forceShowOnlyInput && isActionListVisible
          ? (
            <div style={{ textAlign: 'left' }}>
              <ActionList
                takeMeHome={takeMeHome}
                locateMe={locateMe}
                takePrefilled={takePrefilled}
                preFilledFormatedAddress={preFilledAddress && formatAddress(preFilledAddress)}
                showTakeMeHome={homeLocation !== undefined}
              />
            </div>
          )
          : null}
      </div>
    </div>
  )
}

export default React.memo(LocationPicker)
